import React, { useState, useRef, useContext, useEffect } from 'react';
import EditableField from '../../components/shared/editable-input';
import { RxCross2 } from "react-icons/rx";
import ModalHeading from '../../components/shared/modal-heading';
import { AccountsContext } from '../../components/context/account-context';
import { prependDefault } from '../../utils/structure-options-utils';
import { uploadMedia } from '../../services/upload-service';
import { updateFuel } from '../../services/fuels-service';
import AttachmentInput from '../../components/shared/attachment-input';
import Loader from '../../components/shared/loader';

const EditModal = ({ selectedRow, setSelectedRow, globalDataloading, setIsEditModalOpen, statusOption, setData, fetchFuels, isVehicleTab = false, spendors, vendors, vehicles, }) => {
    const { accountId, attributes } = useContext(AccountsContext);
    const [imagePreview, setImagePreview] = useState(selectedRow?.expense?.media?.signedThumbUrl || null);
    const [uploadStatus, setUploadStatus] = useState({ message: '', type: '' });
    const [updateStatus, setUpdateStatus] = useState({ message: '', type: '' });
    const [loadingFields, setLoadingFields] = useState({});
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef(null);

    // Clear status messages after 2 seconds
    useEffect(() => {
        let timer;
        if (uploadStatus.message && uploadStatus.type !== 'info') {
            timer = setTimeout(() => {
                setUploadStatus({ message: '', type: '' });
            }, 2000);
        }
        return () => clearTimeout(timer);
    }, [uploadStatus]);

    useEffect(() => {
        let timer;
        if (updateStatus.message && updateStatus.type !== 'info') {
            timer = setTimeout(() => {
                setUpdateStatus({ message: '', type: '' });
            }, 2000);
        }
        return () => clearTimeout(timer);
    }, [updateStatus]);

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (file) {
            setIsUploading(true);
            const reader = new FileReader();
            reader.onloadend = async () => {
                setImagePreview(reader.result);

                try {
                    const response = await uploadMedia(accountId, file, 'expense', selectedRow.expense.expenseId);

                    if (response.success) {
                        const updatedRow = {
                            ...selectedRow,
                            expense: {
                                ...selectedRow.expense,
                                media: response.data
                            }
                        };
                        setSelectedRow(updatedRow);
                        setData(prevData => prevData.map(row =>
                            row.fuelId === selectedRow.fuelId ? updatedRow : row
                        ));
                        setUploadStatus({ message: 'Receipt uploaded successfully!', type: 'success' });
                    } else {
                        setImagePreview(null);
                        setUploadStatus({
                            message: response.data.message || 'Failed to upload receipt',
                            type: 'error'
                        });
                    }
                } finally {
                    setIsUploading(false);
                }
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveImage = () => {
        if (isUploading) return;
        const updatedRow = {
            ...selectedRow,
            expense: {
                ...selectedRow.expense,
                media: null
            }
        };
        setImagePreview(null);
        setSelectedRow(updatedRow);
        setData(prevData => prevData.map(row =>
            row.fuelId === selectedRow.fuelId ? updatedRow : row
        ));
        setUploadStatus({ message: '', type: '' });
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleFieldUpdate = async (value, fieldName) => {
        console.log(value, fieldName)
        setLoadingFields(prev => ({ ...prev, [fieldName]: true }));
        setUpdateStatus({ message: '', type: '' });

        const fieldMap = {
            time: 'fuelTime',
            vendor: 'vendorId',
            vehicle: 'vehicleId',
            odometer: 'odometer',
            quantity: 'quantity',
            amount: 'amount',
            type: 'type',
            status: 'status',
            spender: 'spentBy',
            note: 'note'
        };

        const payload = {};
        if (fieldName === 'time') {
            payload[fieldMap[fieldName]] = new Date(value).toISOString();
        } else if (fieldName === 'vendor') {
            payload[fieldMap[fieldName]] = value;
        } else if (fieldName === 'vehicle') {
            payload[fieldMap[fieldName]] = value;
        } else if (fieldName === 'spender') {
            payload[fieldMap[fieldName]] = value;
        } else if (fieldName === 'amount') {
            payload[fieldMap[fieldName]] = parseFloat(value);
        } else if (fieldName === 'odometer') {
            payload[fieldMap[fieldName]] = parseInt(value);
        } else if (fieldName === 'quantity') {
            payload[fieldMap[fieldName]] = parseFloat(value);
        } else {
            payload[fieldMap[fieldName]] = value;
        }

        console.log(payload)
        try {
            const response = await updateFuel(accountId, selectedRow.fuelId, payload);
            console.log(response)
            if (response.success) {
                const updatedRow = { ...selectedRow };
                switch (fieldName) {
                    case 'time':
                        updatedRow.fuelTime = payload.fuelTime;
                        break;
                    case 'vendor':
                        updatedRow.vendor = {
                            vendorId: value,
                            name: vendorOptions.find(opt => opt.value === value)?.label || ''
                        };
                        break;
                    case 'vehicle':
                        updatedRow.vehicle = {
                            vehicleId: value,
                            name: vehicleOptions.find(opt => opt.value === value)?.label || ''
                        };
                        break;
                    case 'spender':
                        updatedRow.spender = {
                            userId: value,
                            name: spenderOptions.find(opt => opt.value === value)?.label || ''
                        };
                        break;
                    case 'amount':
                        updatedRow.expense = { ...updatedRow.expense, amount: parseFloat(value) };
                        break;
                    default:
                        updatedRow[fieldName] = value;
                }
                setSelectedRow(updatedRow);
                setData(prevData => prevData.map(row =>
                    row.fuelId === selectedRow.fuelId ? updatedRow : row
                ));
                setUpdateStatus({ message: `${labelMap[fieldName] || fieldName} updated successfully!`, type: 'success' });
            } else {
                setUpdateStatus({
                    message: response.data.message || `Failed to update ${labelMap[fieldName] || fieldName}`,
                    type: 'error'
                });
                // throw new Error('API update failed');
            }
        } catch (error) {
            setUpdateStatus({
                message: error.message || `Failed to update ${labelMap[fieldName] || fieldName}`,
                type: 'error'
            });
            throw error;
        } finally {
            setLoadingFields(prev => ({ ...prev, [fieldName]: false }));
        }
    };

    const labelMap = {
        time: 'Time',
        vendor: 'Vendor',
        vehicle: 'Vehicle',
        odometer: 'Odometer',
        quantity: 'Quantity',
        amount: 'Amount',
        type: 'Fuel Type',
        status: 'Status',
        spender: 'Spender',
        note: 'Note'
    };

    const triggerFileInput = () => {
        fileInputRef.current.click();
    };

    const typeOptions = [
        { value: 'diesel', label: 'Diesel' },
        { value: 'gasoline', label: 'Gasoline' },
    ];

    const vendorOptions = prependDefault(vendors.map(v => ({ value: v.vendorId, label: v.label })), 'Vendor');
    const vehicleOptions = prependDefault(vehicles.map(v => ({ value: v.vehicleId, label: v.label })), 'Vehicle');
    const spenderOptions = prependDefault(spendors.map(s => ({ value: s.userId, label: s.label })), 'Spender');
    const statusOptions = prependDefault(statusOption.map(s => ({ value: s.value, label: s.label })), 'Status');


    // if (globalDataLoading) return <Loader message={'Loading data...'} />;

    return (
        <div className="space-y-3 px-4 pt-3 relative">
            <ModalHeading className="text-left mb-3">Fuel Detail Information</ModalHeading>

            <button
                className='hover:scale-105 absolute -top-3 -right-2 bg-white border p-2 rounded-full'
                onClick={() => setIsEditModalOpen(false)}
            >
                <RxCross2 className='text-xl' />
            </button>

            {(uploadStatus.message || updateStatus.message) && (
                <div className={`px-4 py-2 text-sm ${(uploadStatus.type || updateStatus.type) === 'success' ? 'text-green-600 bg-green-100' :
                    (uploadStatus.type || updateStatus.type) === 'error' ? 'text-red-600 bg-red-100' :
                        'text-blue-600 bg-blue-100'
                    } rounded-md mx-4`}>
                    {uploadStatus.message || updateStatus.message}
                </div>
            )}

            {
                globalDataloading ?
                    <Loader />
                    :
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <EditableField
                            label="Time"
                            name="time"
                            value={selectedRow?.fuelTime || ''}
                            onChange={handleFieldUpdate}
                            type="datetime-local"
                            isLoading={loadingFields.time}
                        />

                        <EditableField
                            label="Vendor"
                            name="vendor"
                            type='select'
                            options={vendorOptions}
                            value={selectedRow?.vendor?.vendorId || ''}
                            onChange={handleFieldUpdate}
                            isLoading={loadingFields.vendor}
                        />

                        <EditableField
                            label="Vehicle"
                            name="vehicle"
                            type='select'
                            value={selectedRow?.vehicle?.vehicleId || ''}
                            options={vehicleOptions}
                            onChange={handleFieldUpdate}
                            isEditable={!isVehicleTab}
                            isLoading={loadingFields.vehicle}
                        />

                        <EditableField
                            label="Odometer"
                            name="odometer"
                            value={selectedRow?.odometer ?? ''}
                            onChange={handleFieldUpdate}
                            type="number"
                            isLoading={loadingFields.odometer}
                            unit={attributes?.distanceUnit}
                        />

                        <EditableField
                            label="Quantity"
                            name="quantity"
                            value={selectedRow?.quantity ?? ''}
                            onChange={handleFieldUpdate}
                            type="number"
                            isLoading={loadingFields.quantity}
                        />

                        <EditableField
                            label="Amount"
                            name="amount"
                            value={selectedRow?.expense?.amount ?? ''}
                            onChange={handleFieldUpdate}
                            type="number"
                            isEditable={false}
                            isLoading={loadingFields.amount}
                            unit={attributes?.currency}
                        />

                        <EditableField
                            label="Type"
                            name="type"
                            type="select"
                            value={selectedRow?.type || ''}
                            onChange={handleFieldUpdate}
                            options={typeOptions}
                            isLoading={loadingFields.type}
                        />

                        <EditableField
                            label="Status"
                            name="status"
                            value={selectedRow?.status || 'submitted'}
                            onChange={handleFieldUpdate}
                            type="select"
                            options={statusOptions}
                            isEditable={false}
                            isLoading={loadingFields.status}
                        />

                        <EditableField
                            label="Spender"
                            name="spender"
                            type='select'
                            options={spenderOptions}
                            value={selectedRow?.spender?.userId || ''}
                            onChange={handleFieldUpdate}
                            isLoading={loadingFields.spender}
                        />

                        <EditableField
                            label="Note"
                            name="note"
                            type='textarea'
                            rows={2}
                            value={selectedRow?.note || ''}
                            onChange={handleFieldUpdate}
                            isLoading={loadingFields.note}
                        />

                        <AttachmentInput
                            fileInputRef={fileInputRef}
                            handleImageUpload={handleImageUpload}
                            triggerFileInput={triggerFileInput}
                            imagePreview={imagePreview}
                            selectedRow={selectedRow}
                            handleRemoveImage={handleRemoveImage}
                            isUploading={isUploading}
                            accountId={accountId}
                        />
                    </div>
            }


        </div>
    );
};

export default EditModal; 